<template>
    <div>
        <div
            class="tw-grid tw-grid-cols-1 tw-gap-8"
            :class="{
                // for postcss to identify used classes
                'md:tw-grid-cols-2': maxCols === 2,
                'md:tw-grid-cols-3': maxCols === 3,
                'md:tw-grid-cols-4': maxCols === 4,
            }"
        >
            <div
                v-for="option in options"
                :key="option._id"
                class="tw-relative tw-rounded tw-bg-gray-100 tw-border-2 tw-border-solid tw-border-gray-200 tw-text-center tw-select-none"
                :class="{
                    'tw-border-indigo-500': form.selectedOptions.includes(option._id),
                }"
            >
                <label
                    class="tw-relative tw-flex tw-m-0 tw-h-full tw-p-4 tw-pl-[72px] tw-cursor-pointer tw-text-gray-500 focus:tw-text-indigo-500 ignore-enter-submit"
                    :class="{
                        'tw-justify-center tw-items-center': maxCols !== 1,
                        'tw-text-left': maxCols === 1,
                    }"
                    :for="`option-${option._id}`"
                    tabindex="0"
                    data-test-class="survey-input-option"
                    @keyup.enter="
                        onSelect($event, option._id, !form.selectedOptions.includes(option._id))
                    "
                >
                    <div
                        class="tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-h-full tw-w-14 tw-bg-gray-300 tw-opacity-20"
                    />

                    <div
                        class="tw-bg-white tw-border-2 tw-border-solid tw-border-gray-200 tw-text-sm tw-w-6 tw-h-6 tw-absolute tw-top-1/2 tw-transform tw--translate-y-1/2 tw-left-4 tw-flex tw-items-center tw-justify-center"
                        :class="{
                            'tw-rounded-full': !allowsMultipleSelections,
                            'tw-rounded': allowsMultipleSelections,
                            'tw-bg-indigo-500 tw-border-indigo-500 tw-text-indigo-50': form.selectedOptions.includes(
                                option._id,
                            ),
                        }"
                    >
                        <i
                            v-show="form.selectedOptions.includes(option._id)"
                            class="fa fa-check tw-mt-px"
                        />
                    </div>

                    {{ option.title }}

                    <input
                        :id="`option-${option._id}`"
                        class="tw-hidden"
                        type="checkbox"
                        :value="option._id"
                        :checked="form.selectedOptions.includes(option._id)"
                        @change="onSelect($event, $event.target.value, $event.target.checked)"
                    />
                </label>
            </div>
        </div>

        <slot />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    props: {
        question: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            form: {
                selectedOptions: [],
            },
        };
    },
    computed: {
        ...mapGetters({
            userAnswers: "surveys/answerSession/userAnswers",
        }),
        options() {
            return Array.isArray(this.question.skill.multipleChoiceOptions)
                ? this.question.skill.multipleChoiceOptions
                : [];
        },
        allowsMultipleSelections() {
            return !!this.question?.skill.allowMultipleAnswers;
        },
        maxCols() {
            // add some artificial variation
            const canDisplayMultiple = this.options.every(option => option.title?.length <= 30);

            if (!canDisplayMultiple) return 1;

            // max of 4 in a row
            if (this.options.length > 4) return 4;

            return this.options.length;
        },
    },
    created() {
        // prefill form from global state
        if (this.userAnswers?.[this.question?.skill_id]) {
            this.form = { ...this.form, ...this.userAnswers?.[this.question.skill._id] };
        }
    },
    methods: {
        onSaveAnswer() {
            const payload = {
                questionId: this.question.skill._id,
                answer: { ...this.form },
            };

            // validation
            if (!payload.answer.selectedOptions?.length) delete payload.answer;

            this.$store.dispatch("surveys/answerSession/setAnswer", { payload });
        },
        onSelect(event, value, checked) {
            if (checked) {
                // add selected option
                if (this.allowsMultipleSelections) {
                    const values = [...this.form.selectedOptions];
                    if (!values.includes(value)) values.push(value);
                    this.form.selectedOptions = values;
                } else {
                    this.form.selectedOptions = [value];
                }
            } else {
                // remove selected option
                if (this.allowsMultipleSelections) {
                    this.form.selectedOptions = this.form.selectedOptions.filter(
                        option => value !== option,
                    );
                } else {
                    this.form.selectedOptions = [];
                }
            }

            // to allow submit on enter, we remove the focus when no keyboard is used
            if (!(event instanceof KeyboardEvent) && !event.code) {
                if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
            }

            this.onSaveAnswer();
        },
    },
};
</script>

<style scoped>
/* possible with tailwind-jit */
.tw-pl-\[72px\] {
    padding-left: 72px;
}
</style>
